/**
 * /pages/quote — review items in the storefront quote cart, edit
 * quantities, calculate a shipping estimate, and submit the whole batch
 * to the backend's /apps/quotes/intake endpoint.
 *
 * State lives in QuoteCart (localStorage). The DOM is hydrated client-side
 * because Shopify's Liquid layer can't see localStorage.
 */
// Sentinel value for the customer-carrier-account radio. Doubled-up
// underscores so it can't collide with a real Shopify rate handle (those
// are JWT-shaped strings).
const CARRIER_ACCOUNT_VALUE = '__carrier_account__';

// Billing-address fields that become required when the "different than
// shipping" checkbox is on. Mirrors the shipping side: every field
// except the apartment/suite/unit line. The toggle handler flips the
// `required` attribute on these so validateRequiredFields (which scans
// for `[required]`) picks them up only when the section is visible.
const BILLING_REQUIRED_FIELDS = [
  'billing_street1',
  'billing_city',
  'billing_state',
  'billing_zip_code',
  'billing_country',
];

// Admin attachment cap, mirroring the backend's per-file limit. Caught
// client-side so admins don't sit through an upload that the server
// will reject anyway.
const ADMIN_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

if (!customElements.get('quote-page')) {
  customElements.define(
    'quote-page',
    class QuotePage extends HTMLElement {
      connectedCallback() {
        this.itemsEl = this.querySelector('[data-quote-items]');
        this.emptyEl = this.querySelector('[data-quote-empty]');
        this.formEl = this.querySelector('[data-quote-form]');
        this.successEl = this.querySelector('[data-quote-success]');
        this.statusEl = this.querySelector('[data-quote-status]');
        this.shippingStatus = this.querySelector('[data-quote-shipping-status]');
        this.shippingOptionsEl = this.querySelector('[data-quote-shipping-options]');
        this.calcShippingBtn = this.querySelector('[data-quote-calc-shipping]');
        this.submitBtn = this.querySelector('[data-quote-submit]');
        this.phoneInput = this.querySelector('[data-phone-input]');
        this.loadingOverlay = this.querySelector('[data-quote-loading-overlay]');

        // Customer-carrier-account option: a separate radio (mutually
        // exclusive with the calculated-rate radios) that reveals carrier
        // + account-number fields when picked.
        this.carrierRadio = this.querySelector('[data-quote-carrier-radio]');
        this.carrierFields = this.querySelector('[data-quote-carrier-fields]');
        this.carrierSelect = this.querySelector('[data-quote-carrier-select]');
        this.carrierAccountInput = this.querySelector('[data-quote-carrier-account]');
        this.carrierServiceInput = this.querySelector('[data-quote-carrier-service]');

        this.billingToggle = this.querySelector('[data-quote-billing-toggle]');
        this.billingFields = this.querySelector('[data-quote-billing-fields]');
        this.billingToggle?.addEventListener('change', this.handleBillingToggle.bind(this));

        // Admin mode: anyone signed in with an @stack-light.com Shopify
        // customer email gets the email-customization panel. The data
        // attribute is rendered server-side by Liquid; backend re-
        // verifies via Shopify Admin API on each privileged call, so
        // this is UI-affordance only — not the security boundary.
        // Resolved before applyBillingVisibility() so the admin-only
        // "no required fields" rule wins on the initial paint too.
        this.isAdmin = this.dataset.admin === '1';

        // Initialise to the current checkbox state — handles back-button
        // navigations where the browser restores the checked state but the
        // hidden attribute on the wrapper isn't re-evaluated by the DOM.
        this.applyBillingVisibility();
        this.adminPanel = this.querySelector('[data-quote-admin-panel]');
        this.adminFieldset = this.querySelector('[data-quote-admin-fieldset]');
        this.adminHint = this.querySelector('[data-quote-admin-hint]');
        this.adminStatusEl = this.querySelector('[data-quote-admin-status]');
        this.adminFromInput = this.querySelector('[data-quote-admin-from]');
        this.adminToInput = this.querySelector('[data-quote-admin-to]');
        this.adminCcInput = this.querySelector('[data-quote-admin-cc]');
        this.adminBccInput = this.querySelector('[data-quote-admin-bcc]');
        this.adminCcExtrasEl = this.querySelector('[data-quote-admin-cc-extras]');
        this.adminBccExtrasEl = this.querySelector('[data-quote-admin-bcc-extras]');
        this.adminCcAddBtn = this.querySelector('[data-quote-admin-cc-add]');
        this.adminBccAddBtn = this.querySelector('[data-quote-admin-bcc-add]');
        this.adminSubjectInput = this.querySelector('[data-quote-admin-subject]');
        this.adminBodyInput = this.querySelector('[data-quote-admin-body]');
        this.adminAttachmentsEl = this.querySelector('[data-quote-admin-attachments]');
        this.adminAttachInput = this.querySelector('[data-quote-admin-attach-input]');
        this.adminSendBtn = this.querySelector('[data-quote-admin-send]');
        // Admin-only "Discount %" input. Rendered inside the main form
        // (not the email-customization panel) because the value has to be
        // baked into the Estimate at /generate time, before this fieldset
        // is enabled.
        this.discountInput = this.querySelector('[data-quote-discount-percent]');
        // Reactive total readout that sits next to the discount input.
        // Updates whenever the cart, shipping selection, or discount %
        // changes — gives the admin a preview of the bottom-line figure
        // the QBO Estimate will land on.
        this.totalEl = this.querySelector('[data-quote-total]');
        this.discountRowEl = this.querySelector('[data-quote-discount-row]');
        this.discountAmountEl = this.querySelector('[data-quote-discount-amount]');
        this.totalsNoteEl = this.querySelector('[data-quote-totals-note]');
        this.discountInput?.addEventListener('input', () => this.renderTotals());
        // Extra-attachment File objects the admin picked. Survives until
        // the admin actually clicks Send (or the page is reloaded).
        this.adminExtraAttachments = [];
        // log_id returned from /generate — required for the /send-email
        // call. Stays null until Generate succeeds.
        this.adminLogId = null;

        this.adminAttachInput?.addEventListener('change', this.handleAdminAttachPick.bind(this));
        this.adminSendBtn?.addEventListener('click', this.sendAdminEmail.bind(this));
        this.adminFromInput?.addEventListener('input', this.handleAdminFromChange.bind(this));

        this.adminCcAddBtn?.addEventListener('click', () => this.addRecipientRow('cc'));
        this.adminBccAddBtn?.addEventListener('click', () => this.addRecipientRow('bcc'));
        // Delegated click on the extras container — each "×" remove button
        // dropped a row at a time without per-row listener bookkeeping.
        this.adminCcExtrasEl?.addEventListener('click', this.handleRecipientRemove.bind(this));
        this.adminBccExtrasEl?.addEventListener('click', this.handleRecipientRemove.bind(this));

        // Admin-only "Test quote" checkbox. When checked, the submit body
        // carries `test_quote: true`; on a successful /generate response we
        // auto-prompt the admin to delete the QBO Estimate + Drive PDF + roll
        // back the SL counter so test quotes don't permanently consume an
        // SL{n} number.
        this.testQuoteCheckbox = this.querySelector('[data-quote-test-checkbox]');

        // Admin-only customer-memo textarea. Lands on the QBO Estimate as
        // CustomerMemo, which the QBO PDF renderer prints as the "Message
        // displayed on estimate" block. Only the /generate path forwards
        // it; /intake ignores even if a non-admin client somehow sets it.
        this.customerMemoInput = this.querySelector('[data-quote-customer-memo]');

        // Admin-only "+ Add custom product" affordance and its form. Lets the
        // admin add a one-off line (name / description / price / qty) that
        // isn't in the Shopify catalog. The same form is reused to edit an
        // existing custom line (this.editingCustomId tracks which). Markup is
        // server-side gated to admins, so these are null for customers.
        this.addCustomBtn = this.querySelector('[data-quote-add-custom]');
        this.customForm = this.querySelector('[data-quote-custom-form]');
        this.customNameInput = this.querySelector('[data-quote-custom-name]');
        this.customDescInput = this.querySelector('[data-quote-custom-description]');
        this.customPriceInput = this.querySelector('[data-quote-custom-price]');
        this.customQtyInput = this.querySelector('[data-quote-custom-qty]');
        this.customErrorEl = this.querySelector('[data-quote-custom-error]');
        this.customSubmitBtn = this.querySelector('[data-quote-custom-submit]');
        this.customCancelBtn = this.querySelector('[data-quote-custom-cancel]');
        this.editingCustomId = null;

        this.addCustomBtn?.addEventListener('click', () => this.openCustomForm(null));
        this.customSubmitBtn?.addEventListener('click', () => this.submitCustomForm());
        this.customCancelBtn?.addEventListener('click', () => this.closeCustomForm());

        this.shippingRates = [];
        this.selectedShippingHandle = null;
        this.shippingFetchToken = 0;

        this.formEl?.addEventListener('submit', this.submit.bind(this));
        this.calcShippingBtn?.addEventListener('click', this.fetchShippingRates.bind(this));
        this.phoneInput?.addEventListener('input', this.formatPhoneInput.bind(this));

        // Single delegated change handler on the form covers both the
        // static carrier-account radio and the dynamically rendered rate
        // radios — saves us re-binding on every renderShippingState call.
        this.formEl?.addEventListener('change', this.handleShippingChange.bind(this));

        // Click-on-already-checked = deselect. Standard radio behavior
        // is "once you pick, you can only switch to another", but the
        // helper text above the shipping section advertises "skip and
        // your sales rep will work it out" — give the customer a way
        // back to that no-selection state without forcing them to pick
        // something else first. The mousedown captures the pre-click
        // checked state so the click handler can tell apart "first
        // click that just selected" vs "second click of same radio".
        this._carrierWasCheckedAtMousedown = false;
        this.carrierRadio?.addEventListener('mousedown', () => {
          this._carrierWasCheckedAtMousedown = !!this.carrierRadio?.checked;
        });
        this.carrierRadio?.addEventListener('click', () => {
          if (!this._carrierWasCheckedAtMousedown) return;
          if (!this.carrierRadio) return;
          this.carrierRadio.checked = false;
          this.selectedShippingHandle = null;
          if (this.carrierFields) this.carrierFields.hidden = true;
          this._carrierWasCheckedAtMousedown = false;
          this.renderTotals();
        });

        this.changeUnsubscriber = window.QuoteCart?.onChange(() => this.render());
        this.render();

        // Backfill cached prices for any cart items added before
        // `price_cents` was captured at add-time. Runs once per page load;
        // hits Shopify's public /products/<handle>.js endpoint, which
        // includes the full variant list with prices.
        this.backfillMissingPrices();
      }

      handleBillingToggle() {
        this.applyBillingVisibility();
      }

      applyBillingVisibility() {
        const enabled = !!this.billingToggle?.checked;
        if (this.billingFields) this.billingFields.hidden = !enabled;
        // Toggle `required` so validateRequiredFields skips billing
        // fields when the section is collapsed. Querying by name keeps
        // this in sync regardless of whether the field is an <input>
        // or <select> (the state dropdown comes from a snippet).
        // Admins get the relaxed (email-only) rules everywhere, so we
        // never set `required` on billing fields for them either.
        for (const fieldName of BILLING_REQUIRED_FIELDS) {
          const field = this.formEl?.elements?.namedItem(fieldName);
          if (!field) continue;
          if (enabled && !this.isAdmin) {
            field.setAttribute('required', '');
          } else {
            field.removeAttribute('required');
          }
        }
      }

      handleShippingChange(event) {
        // Two inputs both feed `selectedShippingHandle` and must stay
        // mutually exclusive:
        //   - the static "Use my carrier account" radio (name="shipping_option")
        //   - the dynamically rendered rate <select> (data-quote-shipping-rate)
        // Picking one clears the other so the UI matches the underlying state.
        const target = event.target;
        if (target.name === 'shipping_option') {
          this.selectedShippingHandle = target.value;
          const isCarrierAccount =
            this.selectedShippingHandle === CARRIER_ACCOUNT_VALUE;
          if (this.carrierFields) this.carrierFields.hidden = !isCarrierAccount;
          if (isCarrierAccount) {
            const rateSelect = this.shippingOptionsEl?.querySelector(
              '[data-quote-shipping-rate]'
            );
            if (rateSelect) rateSelect.value = '';
          }
          this.renderTotals();
          return;
        }
        if (target.matches?.('[data-quote-shipping-rate]')) {
          this.selectedShippingHandle = target.value || null;
          if (this.selectedShippingHandle && this.carrierRadio?.checked) {
            this.carrierRadio.checked = false;
            if (this.carrierFields) this.carrierFields.hidden = true;
          }
          this.renderTotals();
        }
      }

      disconnectedCallback() {
        this.changeUnsubscriber?.();
      }

      render() {
        const items = window.QuoteCart?.items() ?? [];
        const isEmpty = items.length === 0;

        if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
        if (this.formEl) this.formEl.hidden = isEmpty;
        if (this.successEl?.hidden === false) {
          // After a successful submit, stay on the success view even if a
          // sneaky background tab clears the cart and re-fires :change.
          return;
        }

        if (!this.itemsEl) return;
        this.itemsEl.replaceChildren(...items.map((item) => this.renderItem(item)));
        this.renderTotals();
      }

      async backfillMissingPrices() {
        const items = window.QuoteCart?.items() ?? [];
        // Group items needing prices by product handle so we only fetch
        // each product's JSON once even if multiple variants of it are in
        // the cart.
        const byHandle = new Map();
        for (const item of items) {
          if (Number.isFinite(item.price_cents)) continue;
          const handle = (item.product_url || '').split('/products/')[1]?.split(/[?#]/)[0];
          if (!handle) continue;
          if (!byHandle.has(handle)) byHandle.set(handle, []);
          byHandle.get(handle).push(item);
        }
        if (byHandle.size === 0) return;

        await Promise.all(
          Array.from(byHandle.entries()).map(async ([handle, group]) => {
            try {
              const response = await fetch(`/products/${handle}.js`, {
                headers: { Accept: 'application/json' },
              });
              if (!response.ok) return;
              const product = await response.json();
              const variants = Array.isArray(product?.variants) ? product.variants : [];
              for (const item of group) {
                const variant = variants.find((v) => String(v.id) === String(item.variant_id));
                if (variant && Number.isFinite(variant.price)) {
                  window.QuoteCart?.setPrice(item.variant_id, variant.price);
                }
              }
            } catch (error) {
              console.warn('[quote-page] price backfill failed for', handle, error);
            }
          })
        );

        // `setPrice` writes to localStorage but doesn't fire a cart-change
        // event (it's a passive metadata refresh, not a content change),
        // so trigger a totals re-render explicitly.
        this.renderTotals();
      }

      renderTotals() {
        // Admin-only block; bail early when the readout isn't rendered.
        if (!this.totalEl) return;

        const items = window.QuoteCart?.items() ?? [];
        // Cart stores prices in cents to avoid float drift; sum as cents
        // and only convert at the very end. An item without a cached
        // price (older add, or product page that didn't ship the data
        // attribute) zero-counts and triggers the "estimate may be off"
        // note below.
        let subtotalCents = 0;
        let missingPrice = false;
        for (const item of items) {
          const qty = Number.parseFloat(item.quantity) || 0;
          const price = Number.isFinite(item.price_cents) ? item.price_cents : null;
          if (price === null) {
            missingPrice = true;
            continue;
          }
          subtotalCents += Math.round(price * qty);
        }

        // Calculated shipping rates feed into the total; customer-carrier-
        // account selections don't (no rate to add). Matches what the
        // backend will record as `shipping_amount` on the QBO Estimate.
        const shipping = this.selectedShipping;
        const shippingCents =
          shipping?.type === 'calculated' ? Math.round((shipping.amount || 0) * 100) : 0;

        const percent = this.discountPercentPayload ?? 0;
        const preDiscountCents = subtotalCents + shippingCents;
        const discountCents = Math.round((preDiscountCents * percent) / 100);
        const totalCents = preDiscountCents - discountCents;

        this.totalEl.textContent = formatPrice(totalCents / 100, 'USD');

        // Show the "Discount -$X.XX" preview line only when a discount is
        // actually being applied; otherwise the total row stands alone.
        if (this.discountRowEl) {
          const show = percent > 0 && discountCents > 0;
          this.discountRowEl.hidden = !show;
          if (show && this.discountAmountEl) {
            this.discountAmountEl.textContent = `-${formatPrice(discountCents / 100, 'USD')}`;
          }
        }

        if (this.totalsNoteEl) {
          if (missingPrice) {
            this.totalsNoteEl.textContent =
              'Note: one or more items have no cached price; total may be off. Final QBO Estimate will use live Shopify prices.';
            this.totalsNoteEl.hidden = false;
          } else {
            this.totalsNoteEl.hidden = true;
          }
        }
      }

      renderItem(item) {
        const row = document.createElement('div');
        row.className = 'quote-page__item';
        row.dataset.variantId = item.variant_id;

        // Only build the image column when there's an image. Custom line
        // items have no thumbnail; without this the body would be squeezed
        // into the leftover space beside an empty image track (the row gets
        // the `--no-media` modifier so it lays out full-width instead).
        let media = null;
        if (item.image_url) {
          media = document.createElement('div');
          media.className = 'quote-page__item-media';
          const img = document.createElement('img');
          img.src = item.image_url;
          img.alt = item.product_title || '';
          img.loading = 'lazy';
          media.appendChild(img);
        } else {
          row.classList.add('quote-page__item--no-media');
        }

        const body = document.createElement('div');
        body.className = 'quote-page__item-body';

        const title = document.createElement('p');
        title.className = 'quote-page__item-title';
        if (item.product_url) {
          const link = document.createElement('a');
          link.href = item.product_url;
          link.textContent = item.product_title || 'Item';
          title.appendChild(link);
        } else {
          title.textContent = item.product_title || 'Item';
        }
        body.appendChild(title);

        // Description line. For catalog items this is the (optionally
        // admin-edited) variant title; for custom items it's the typed
        // description. Admins get an underlined "Edit" affordance at the end:
        // catalog → inline description editor (this quote only, never touches
        // the QBO Item); custom → reopens the custom-product form prefilled.
        const variantText =
          item.variant_title && item.variant_title !== 'Default Title'
            ? item.variant_title
            : '';
        const descText = item.custom ? (item.description || '') : (item.description || variantText);
        if (descText || this.isAdmin) {
          const variant = document.createElement('p');
          variant.className = 'quote-page__item-variant';
          const descSpan = document.createElement('span');
          descSpan.className = 'quote-page__item-desc-text';
          descSpan.textContent = descText;
          variant.appendChild(descSpan);
          if (this.isAdmin) {
            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'quote-page__edit link';
            editBtn.textContent = 'Edit';
            editBtn.addEventListener('click', () => {
              if (item.custom) {
                this.openCustomForm(item);
              } else {
                this.beginDescriptionEdit(variant, item);
              }
            });
            variant.appendChild(document.createTextNode(' '));
            variant.appendChild(editBtn);
          }
          body.appendChild(variant);
        }

        if (item.sku && !item.custom) {
          const sku = document.createElement('p');
          sku.className = 'quote-page__item-sku';
          sku.textContent = `SKU: ${item.sku}`;
          body.appendChild(sku);
        }

        const controls = document.createElement('div');
        controls.className = 'quote-page__item-controls';

        const qtyLabel = document.createElement('label');
        qtyLabel.className = 'quote-page__qty-label';
        qtyLabel.textContent = 'Qty';

        const qtyInput = document.createElement('input');
        qtyInput.type = 'number';
        qtyInput.className = 'quote-page__qty-input';
        qtyInput.min = '1';
        qtyInput.step = '1';
        qtyInput.value = String(item.quantity);
        qtyInput.addEventListener('change', () => {
          const value = Number.parseFloat(qtyInput.value);
          window.QuoteCart?.setQuantity(item.variant_id, Number.isFinite(value) ? value : 0);
        });
        qtyLabel.appendChild(qtyInput);

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'quote-page__remove link';
        removeBtn.textContent = 'Remove';
        removeBtn.addEventListener('click', () => {
          window.QuoteCart?.remove(item.variant_id);
          // Removing an item invalidates whichever shipping rates we'd
          // already fetched for the previous line-up, so clear them.
          this.resetShipping();
        });

        controls.appendChild(qtyLabel);
        controls.appendChild(removeBtn);
        body.appendChild(controls);

        if (media) row.appendChild(media);
        row.appendChild(body);
        return row;
      }

      /**
       * Swap a catalog line's description text for an inline editor (admin
       * "Edit" flow). Saving writes a per-quote override via
       * QuoteCart.setDescription; cancelling re-renders from the cart,
       * discarding the edit. The underlying QBO/Shopify item is untouched.
       */
      beginDescriptionEdit(descRow, item) {
        const editor = document.createElement('div');
        editor.className = 'quote-page__desc-editor';

        const textarea = document.createElement('textarea');
        textarea.className = 'quote-page__desc-input';
        textarea.rows = 2;
        textarea.value = item.description || (
          item.variant_title && item.variant_title !== 'Default Title'
            ? item.variant_title
            : ''
        );

        const actions = document.createElement('div');
        actions.className = 'quote-page__desc-actions';
        const save = document.createElement('button');
        save.type = 'button';
        save.className = 'quote-page__desc-save link';
        save.textContent = 'Save';
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'quote-page__desc-cancel link';
        cancel.textContent = 'Cancel';
        save.addEventListener('click', () => {
          window.QuoteCart?.setDescription(item.variant_id, textarea.value);
          // setDescription only fires a change event when the value actually
          // changed; re-render explicitly so a no-op Save still closes the
          // editor.
          this.render();
        });
        cancel.addEventListener('click', () => this.render());
        actions.appendChild(save);
        actions.appendChild(cancel);

        editor.appendChild(textarea);
        editor.appendChild(actions);
        descRow.replaceWith(editor);
        textarea.focus();
      }

      /**
       * Open the custom-product form. Pass a custom cart item to edit it
       * (prefills the fields and switches to "Save"), or null to add a new
       * one. Admin-only — the markup is absent for customers.
       */
      openCustomForm(item) {
        if (!this.customForm) return;
        this.editingCustomId = item ? item.variant_id : null;
        if (this.customNameInput) this.customNameInput.value = item ? item.product_title || '' : '';
        if (this.customDescInput) this.customDescInput.value = item ? item.description || '' : '';
        if (this.customPriceInput) {
          this.customPriceInput.value =
            item && Number.isFinite(item.price_cents) ? (item.price_cents / 100).toFixed(2) : '';
        }
        if (this.customQtyInput) this.customQtyInput.value = item ? String(item.quantity) : '1';
        if (this.customErrorEl) {
          this.customErrorEl.textContent = '';
          this.customErrorEl.hidden = true;
        }
        if (this.customSubmitBtn) {
          this.customSubmitBtn.textContent = item ? 'Save' : 'Add to quote';
        }
        this.customForm.hidden = false;
        this.customNameInput?.focus();
      }

      closeCustomForm() {
        this.editingCustomId = null;
        if (this.customForm) this.customForm.hidden = true;
      }

      submitCustomForm() {
        const name = clean(this.customNameInput?.value);
        const description = clean(this.customDescInput?.value);
        const price = Number.parseFloat(this.customPriceInput?.value);
        const qty = Number.parseFloat(this.customQtyInput?.value);

        const showCustomError = (message) => {
          if (!this.customErrorEl) return;
          this.customErrorEl.textContent = message;
          this.customErrorEl.hidden = false;
        };

        if (!name) {
          showCustomError('Enter a product name.');
          this.customNameInput?.focus();
          return;
        }
        if (!Number.isFinite(price) || price < 0) {
          showCustomError('Enter a unit price of 0 or more.');
          this.customPriceInput?.focus();
          return;
        }
        if (!Number.isFinite(qty) || qty <= 0) {
          showCustomError('Enter a quantity of 1 or more.');
          this.customQtyInput?.focus();
          return;
        }

        const priceCents = Math.round(price * 100);
        if (this.editingCustomId) {
          window.QuoteCart?.updateCustom(this.editingCustomId, {
            name,
            description,
            priceCents,
            quantity: qty,
          });
        } else {
          const uniqueId =
            (window.crypto?.randomUUID?.()) ||
            `${Date.now()}-${Math.round(Math.random() * 1e6)}`;
          window.QuoteCart?.add({
            variant_id: `custom:${uniqueId}`,
            custom: true,
            product_title: name,
            description,
            price_cents: priceCents,
            quantity: qty,
          });
        }
        // A custom line has no carrier-rated shipping basis until the rep
        // recalculates, so drop any stale rates like the qty/remove paths do.
        this.resetShipping();
        this.closeCustomForm();
      }

      resetShipping() {
        this.shippingRates = [];
        // Carrier-account is a customer preference independent of which
        // items are in the quote — preserve it across cart edits. Only
        // wipe the handle when the prior selection was a (now-stale)
        // calculated rate.
        if (this.selectedShippingHandle !== CARRIER_ACCOUNT_VALUE) {
          this.selectedShippingHandle = null;
        }
        if (this.shippingStatus) this.shippingStatus.textContent = '';
        if (this.shippingOptionsEl) this.shippingOptionsEl.replaceChildren();
      }

      async fetchShippingRates() {
        const items = window.QuoteCart?.items() ?? [];
        if (items.length === 0) {
          this.shippingStatus.textContent = 'Add an item to your quote before calculating shipping.';
          return;
        }

        const data = new FormData(this.formEl);
        const country = clean(data.get('country'));
        const province = clean(data.get('state'));
        const zip = clean(data.get('zip_code'));

        const missing = [];
        if (!country) missing.push('country');
        if (!province) missing.push('state');
        if (!zip) missing.push('ZIP code');
        if (missing.length > 0) {
          this.resetShipping();
          this.shippingStatus.textContent = `Enter ${missing.join(', ')} to calculate shipping.`;
          return;
        }

        const token = ++this.shippingFetchToken;
        this.renderShippingState('loading');

        try {
          const response = await fetch(this.dataset.ratesEndpoint, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: JSON.stringify({
              items: items.map((i) => ({
                variant_id: String(i.variant_id),
                quantity: i.quantity,
              })),
              country,
              province,
              zip_code: zip,
            }),
          });
          // Discard responses from outdated requests so a slow first query
          // can't overwrite a fresh one's results.
          if (token !== this.shippingFetchToken) return;

          if (!response.ok) {
            this.shippingRates = [];
            this.selectedShippingHandle = null;
            this.renderShippingState('error');
            return;
          }
          const rates = await response.json();
          this.shippingRates = Array.isArray(rates) ? rates : [];
          // Auto-select the first rate when nothing is selected — but
          // never override a customer-carrier-account selection just
          // because they happened to also click Calculate to compare.
          if (
            this.shippingRates.length > 0 &&
            this.selectedShippingHandle !== CARRIER_ACCOUNT_VALUE &&
            !this.shippingRates.find((r) => r.handle === this.selectedShippingHandle)
          ) {
            this.selectedShippingHandle = this.shippingRates[0].handle;
          }
          this.renderShippingState(null);
        } catch (error) {
          if (token !== this.shippingFetchToken) return;
          console.error('[quote-page] shipping fetch failed', error);
          this.shippingRates = [];
          this.selectedShippingHandle = null;
          this.renderShippingState('error');
        }
      }

      renderShippingState(state) {
        if (state === 'loading') {
          this.shippingStatus.textContent = 'Calculating shipping…';
          this.shippingOptionsEl.replaceChildren();
          return;
        }
        if (state === 'error') {
          this.shippingStatus.textContent =
            "Couldn't load rates. Your sales rep will quote shipping separately.";
          this.shippingOptionsEl.replaceChildren();
          return;
        }
        if (this.shippingRates.length === 0) {
          this.shippingStatus.textContent = 'No shipping options available for this address.';
          this.shippingOptionsEl.replaceChildren();
          return;
        }

        this.shippingStatus.textContent = '';

        // Single <select> with one option per rate plus a "no selection"
        // placeholder, instead of one radio-row per rate. Same change
        // handler (handleShippingChange) picks it up via the
        // data-quote-shipping-rate attribute.
        const select = document.createElement('select');
        select.className = 'field__input quote-request__shipping-select';
        select.dataset.quoteShippingRate = '';

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = '— Select a shipping option —';
        if (!this.selectedShippingHandle ||
            this.selectedShippingHandle === CARRIER_ACCOUNT_VALUE) {
          placeholder.selected = true;
        }
        select.appendChild(placeholder);

        for (const rate of this.shippingRates) {
          const option = document.createElement('option');
          option.value = rate.handle;
          option.textContent =
            `${rate.title} (estimate) — ${formatPrice(rate.amount, rate.currency)}`;
          if (rate.handle === this.selectedShippingHandle) option.selected = true;
          select.appendChild(option);
        }

        this.shippingOptionsEl.replaceChildren(select);
      }

      get customerMemoPayload() {
        // Non-admin builds never render the textarea — always null. Empty
        // strings also collapse to null so the QBO Estimate doesn't get a
        // blank CustomerMemo (would still print a "Message:" header).
        if (!this.isAdmin || !this.customerMemoInput) return null;
        const raw = String(this.customerMemoInput.value || '').trim();
        return raw || null;
      }

      get discountPercentPayload() {
        // Non-admin builds never render the input — always null. Empty
        // string / 0 / NaN all collapse to null so the backend doesn't
        // append a 0% discount line.
        if (!this.isAdmin || !this.discountInput) return null;
        const raw = this.discountInput.value?.trim();
        if (!raw) return null;
        const parsed = Number.parseFloat(raw);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        // Clamp to 100; the server also clamps but pre-clamping here means
        // a typo of 1000 doesn't 422 the whole submission.
        return Math.min(100, parsed);
      }

      get selectedShipping() {
        if (this.selectedShippingHandle === CARRIER_ACCOUNT_VALUE) {
          const carrier = clean(this.carrierSelect?.value);
          const accountNumber = clean(this.carrierAccountInput?.value);
          // Validation runs before submit; here we just silently return
          // null when the fields haven't been filled in — keeps the
          // submission shape consistent ("no shipping selected" = null).
          if (!carrier || !accountNumber) return null;
          return {
            type: 'carrier_account',
            carrier,
            account_number: accountNumber,
            service_level: clean(this.carrierServiceInput?.value),
          };
        }
        const rate = this.shippingRates.find((r) => r.handle === this.selectedShippingHandle);
        if (!rate) return null;
        return {
          type: 'calculated',
          handle: rate.handle,
          title: rate.title,
          amount: rate.amount,
          currency: rate.currency,
        };
      }

      async submit(event) {
        event.preventDefault();
        if (!this.validateRequiredFields()) return;

        const items = window.QuoteCart?.items() ?? [];
        if (items.length === 0) {
          this.showError('Your quote is empty.');
          return;
        }

        this.setSubmitting(true);
        this.clearStatus();

        const endpoint = this.isAdmin ? this.dataset.generateEndpoint : this.dataset.endpoint;

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            // Customer flow: strip Shopify session cookies so the proxy
            // routes the request to our backend cleanly (anonymous).
            // Admin flow: keep cookies so Shopify can populate
            // `logged_in_customer_id` in the signed proxy query string —
            // that's the trust anchor for the admin-auth check on the
            // backend. The text/plain content-type still avoids
            // Shopify's JSON-POST intercept in both cases.
            credentials: this.isAdmin ? 'include' : 'omit',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: JSON.stringify({
              items: items.map((i) =>
                i.custom
                  ? {
                      variant_id: String(i.variant_id),
                      custom: true,
                      name: i.product_title || '',
                      description: i.description || null,
                      unit_price: Number.isFinite(i.price_cents) ? i.price_cents / 100 : 0,
                      quantity: i.quantity,
                    }
                  : {
                      variant_id: String(i.variant_id),
                      sku: i.sku || null,
                      quantity: i.quantity,
                      // Admin-only per-line description override; ignored on
                      // /intake. Null for unedited / customer carts.
                      description: i.description || null,
                    }
              ),
              contact: this.contactPayload,
              billing_address: this.billingAddressPayload,
              selected_shipping: this.selectedShipping,
              page_url: window.location.href,
              website: this.formEl.elements.website?.value || null,
              discount_percent: this.discountPercentPayload,
              // Only the admin form has the checkbox; non-admin submits
              // always send false. Backend ignores the flag on /intake.
              test_quote: this.isAdmin && !!this.testQuoteCheckbox?.checked,
              customer_memo: this.customerMemoPayload,
            }),
          });

          const body = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(body.detail || 'Quote submission failed. Please try again.');
          }

          if (this.isAdmin) {
            // Admin flow: don't clear the cart yet (the quote is in QBO
            // but the email hasn't been sent), don't show success — open
            // the admin panel pre-filled and wait for Send.
            this.hydrateAdminPanel(body);
            if (this.testQuoteCheckbox?.checked) {
              // Don't block setSubmitting() in the `finally` below — fire
              // the dialog after the spinner clears so the page looks idle.
              setTimeout(() => this.promptDeleteTestQuote(body), 0);
            }
          } else {
            window.QuoteCart?.clear();
            this.showSuccessView(body?.quote_number);
          }
        } catch (error) {
          console.error('[quote-page] submit failed', error);
          this.showSubmitFailure();
        } finally {
          this.setSubmitting(false);
        }
      }

      async promptDeleteTestQuote(generateResponse) {
        const logId = generateResponse?.id;
        const quoteNumber = generateResponse?.quote_number || '(unknown)';
        if (!logId) return;
        const ok = window.confirm(
          `Test quote ${quoteNumber} created in QuickBooks and Drive.\n\n` +
            'Delete it now from QBO + Drive and roll back the SL counter?\n\n' +
            'OK: delete now.   Cancel: leave it (you can clean up manually in QBO/Drive).'
        );
        if (!ok) {
          this.setAdminStatus(
            `Test quote ${quoteNumber} left in place. Remember to clean it up in QBO and Drive.`,
            true,
          );
          return;
        }
        this.setAdminStatus(`Deleting test quote ${quoteNumber}…`);
        try {
          const endpoint = `${this.dataset.sendEmailEndpointBase}/${logId}/delete-test-quote`;
          const response = await fetch(endpoint, {
            method: 'POST',
            // Same admin-auth shape as /generate and /send-email:
            // cookies on, text/plain to dodge Shopify's JSON interception.
            credentials: 'include',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: '',
          });
          const body = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(body.detail || 'Delete failed.');
          }
          const summary = (body.messages || []).join(' ');
          const failed = body.status === 'partial';
          this.setAdminStatus(
            failed
              ? `Test quote ${quoteNumber} partially deleted — finish manually. ${summary}`
              : `Test quote ${quoteNumber} deleted. ${summary}`,
            failed,
          );
          if (!failed) {
            // Disable the admin panel so the operator doesn't try to
            // Send an email for a quote that no longer exists in QBO.
            if (this.adminFieldset) this.adminFieldset.disabled = true;
            if (this.adminPanel) this.adminPanel.classList.remove('is-ready');
          }
        } catch (error) {
          console.error('[quote-page] delete-test-quote failed', error);
          this.setAdminStatus(
            `Test quote ${quoteNumber} delete failed: ${error.message || error}. Clean up manually in QBO + Drive.`,
            true,
          );
        }
      }

      hydrateAdminPanel(generateResponse) {
        // Generated quote details landing in the admin panel. log_id is
        // the handle the /send-email call will hang off; the rest are
        // defaults the admin can override.
        this.adminLogId = generateResponse?.id ?? null;
        this.adminQuoteNumber = generateResponse?.quote_number || '';
        this.adminCustomerName = generateResponse?.customer_name || '';
        this.adminDefaultFrom = generateResponse?.default_from || '';
        // Only pre-fill the To field when the admin hasn't already typed
        // one. The input now lives outside the disabled fieldset so it
        // can be filled in before Generate — clobbering it on hydrate
        // would silently lose that input. CC/BCC have always been
        // untouched here, so they persist automatically.
        if (this.adminToInput && !this.adminToInput.value.trim()) {
          this.adminToInput.value = generateResponse?.default_to_email || '';
        }
        if (this.adminSubjectInput) this.adminSubjectInput.value = generateResponse?.default_subject || '';
        if (this.adminBodyInput) this.adminBodyInput.value = generateResponse?.default_body || '';
        if (this.adminFromInput) {
          // Pre-fill with the signed-in admin's email (returned as
          // default_from by /generate). Admin can override before
          // sending, but the common case is "send as me" so saving
          // the typing is worth it.
          this.adminFromInput.value = this.adminDefaultFrom;
        }
        // Track the auto-generated body so we can detect "admin edited
        // it" later — if they retype the body, we leave it alone when
        // From changes; if they didn't touch it, we keep the signature
        // in sync with the From field.
        this.adminLastAutoBody = this.adminBodyInput?.value || '';
        this.adminExtraAttachments = [];
        this.renderAdminAttachments(generateResponse?.pdf_filename);
        if (this.adminFieldset) this.adminFieldset.disabled = false;
        if (this.adminPanel) this.adminPanel.classList.add('is-ready');
        if (this.adminHint) {
          const num = generateResponse?.quote_number || '(unknown)';
          this.adminHint.innerHTML =
            `Quote <strong>${num}</strong> created in QuickBooks. Review and Send when ready.`;
        }
        this.setAdminStatus('');
      }

      // Mirrors `parse_sender_name` in backend/app/services/mailgun.py.
      // Keep the two in sync — they both produce the signature name on
      // the quote-email body.
      parseSenderName(fromValue) {
        let s = String(fromValue || '').trim();
        if (!s) return 'Stack-Light';
        if (s.includes('<')) {
          const display = s.slice(0, s.indexOf('<')).trim().replace(/^"|"$/g, '').trim();
          if (display) return display;
          s = s.slice(s.indexOf('<') + 1).split('>')[0];
        }
        const local = s.split('@')[0] || '';
        const parts = local.split(/[._-]/).filter(Boolean);
        if (parts.length === 0) return 'Stack-Light';
        return parts
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
          .join(' ');
      }

      buildAdminBody(customerName, fromValue) {
        const greeting = String(customerName || '').trim() || 'there';
        const sender = this.parseSenderName(fromValue);
        return (
          `Hello ${greeting},\n\n` +
          'Your Stack-Light quote is attached as a PDF.\n\n' +
          'Let me know if you have any questions or changes.\n\n' +
          `Thanks,\n${sender}\n(678) 288-9678`
        );
      }

      handleAdminFromChange() {
        if (!this.adminBodyInput) return;
        // Only rewrite the body if the admin hasn't manually edited it.
        // Comparing against the last auto-generated string is a cheap
        // way to detect that without an explicit "is dirty" flag.
        if (this.adminBodyInput.value !== this.adminLastAutoBody) return;
        const effectiveFrom =
          (this.adminFromInput?.value || '').trim() || this.adminDefaultFrom;
        // Read the customer name fresh from the form rather than the
        // cached value from /generate. The form is the source of truth
        // and stays correct even if the backend response was older
        // (pre-customer_name field) or if the admin edited the name
        // field after generating.
        const customerName =
          String(this.formEl?.elements?.name?.value || '').trim() ||
          this.adminCustomerName ||
          '';
        const newBody = this.buildAdminBody(customerName, effectiveFrom);
        this.adminBodyInput.value = newBody;
        this.adminLastAutoBody = newBody;
      }

      renderAdminAttachments(pdfFilename) {
        if (!this.adminAttachmentsEl) return;
        const items = [];
        if (pdfFilename) {
          items.push({ name: pdfFilename, removable: false });
        }
        this.adminExtraAttachments.forEach((file, idx) => {
          items.push({ name: file.name, removable: true, index: idx });
        });
        this.adminAttachmentsEl.replaceChildren(
          ...items.map((item) => {
            const li = document.createElement('li');
            li.className = 'quote-page__admin-attachment';
            const name = document.createElement('span');
            name.textContent = item.name;
            li.appendChild(name);
            if (item.removable) {
              const remove = document.createElement('button');
              remove.type = 'button';
              remove.className = 'quote-page__admin-attachment-remove link';
              remove.textContent = 'Remove';
              remove.addEventListener('click', () => {
                this.adminExtraAttachments.splice(item.index, 1);
                this.renderAdminAttachments(pdfFilename);
              });
              li.appendChild(remove);
            }
            return li;
          })
        );
      }

      handleAdminAttachPick(event) {
        const files = Array.from(event.target.files || []);
        const accepted = [];
        const rejected = [];
        for (const file of files) {
          if (file.size > ADMIN_ATTACHMENT_MAX_BYTES) {
            rejected.push(`${file.name} (>${ADMIN_ATTACHMENT_MAX_BYTES / (1024 * 1024)}MB)`);
            continue;
          }
          accepted.push(file);
        }
        this.adminExtraAttachments.push(...accepted);
        const pdfFilename = this.adminAttachmentsEl?.querySelector(
          '.quote-page__admin-attachment:first-child span'
        )?.textContent;
        this.renderAdminAttachments(pdfFilename);
        event.target.value = '';
        if (rejected.length > 0) {
          this.setAdminStatus(`Skipped (too large): ${rejected.join(', ')}`, true);
        }
      }

      addRecipientRow(kind) {
        const extras = kind === 'cc' ? this.adminCcExtrasEl : this.adminBccExtrasEl;
        if (!extras) return;
        const labelText = kind === 'cc' ? 'CC' : 'BCC';

        const row = document.createElement('div');
        row.className = 'quote-page__admin-recipient-row';

        const fieldLabel = document.createElement('label');
        fieldLabel.className = 'field';

        const input = document.createElement('input');
        input.className = 'field__input';
        input.autocomplete = 'off';
        // Single-space placeholder keeps Dawn's floating-label `:placeholder-shown`
        // trick working without showing user-visible placeholder text.
        input.placeholder = ' ';

        const span = document.createElement('span');
        span.className = 'field__label';
        span.textContent = labelText;

        fieldLabel.append(input, span);

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'quote-page__admin-recipient-remove';
        removeBtn.setAttribute('aria-label', `Remove ${labelText}`);
        removeBtn.textContent = '×';

        row.append(fieldLabel, removeBtn);
        extras.append(row);
        input.focus();
      }

      handleRecipientRemove(event) {
        const btn = event.target.closest('.quote-page__admin-recipient-remove');
        if (!btn) return;
        btn.closest('.quote-page__admin-recipient-row')?.remove();
      }

      collectRecipients(kind) {
        // Gather the primary input plus every extra row in the group and
        // return them as a single comma-separated string — the shape the
        // /send-email Form-field consumer (and Mailgun beneath it) accepts.
        const primary = kind === 'cc' ? this.adminCcInput : this.adminBccInput;
        const extras = kind === 'cc' ? this.adminCcExtrasEl : this.adminBccExtrasEl;
        const values = [];
        const push = (raw) => {
          const cleaned = String(raw || '').trim();
          if (cleaned) values.push(cleaned);
        };
        push(primary?.value);
        if (extras) {
          extras.querySelectorAll('.field__input').forEach((el) => push(el.value));
        }
        return values.join(', ');
      }

      isValidEmailAddress(token) {
        // Mirror backend _is_valid_email_address: an @, a dot in the domain,
        // no embedded whitespace. Accepts the `Name <addr>` display form by
        // validating only the bracketed address.
        let value = String(token || '').trim();
        if (value.includes('<') && value.includes('>')) {
          value = value.slice(value.indexOf('<') + 1, value.indexOf('>')).trim();
        }
        if (!value || value.includes(' ') || !value.includes('@')) return false;
        const [local, ...domainParts] = value.split('@');
        return Boolean(local) && domainParts.join('@').includes('.');
      }

      async sendAdminEmail() {
        if (this.adminLogId == null) {
          this.setAdminStatus('Generate the quote before sending.', true);
          return;
        }
        const toEmail = String(this.adminToInput?.value || '').trim();
        const subject = String(this.adminSubjectInput?.value || '').trim();
        const body = String(this.adminBodyInput?.value || '').trim();
        if (!toEmail || !toEmail.includes('@')) {
          this.setAdminStatus('Enter a valid To address (must include @).', true);
          this.adminToInput?.focus();
          return;
        }
        // Mirror the backend's address validation (To/From/CC/BCC) so a
        // typo'd address is caught instantly instead of round-tripping to a
        // Mailgun rejection. From is optional; CC/BCC are comma-separated.
        const ccValue = this.collectRecipients('cc');
        const bccValue = this.collectRecipients('bcc');
        const fromValue = String(this.adminFromInput?.value || '').trim();
        const badAddress = (value) =>
          value
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .find((t) => !this.isValidEmailAddress(t));
        for (const [label, value, field] of [
          ['To', toEmail, this.adminToInput],
          ['From', fromValue, this.adminFromInput],
          ['CC', ccValue, this.adminCcInput],
          ['BCC', bccValue, this.adminBccInput],
        ]) {
          const bad = badAddress(value);
          if (bad) {
            this.setAdminStatus(
              `${label} address "${bad}" is not a valid email (must include @ and a domain).`,
              true,
            );
            field?.focus();
            return;
          }
        }
        if (!subject) {
          this.setAdminStatus('Subject is required.', true);
          this.adminSubjectInput?.focus();
          return;
        }
        if (!body) {
          this.setAdminStatus('Email body is required.', true);
          this.adminBodyInput?.focus();
          return;
        }

        const form = new FormData();
        form.append('from_email', fromValue);
        form.append('to_email', toEmail);
        form.append('cc', ccValue);
        form.append('bcc', bccValue);
        form.append('subject', subject);
        form.append('body', body);
        for (const file of this.adminExtraAttachments) {
          form.append('attachments', file, file.name);
        }

        const endpoint = `${this.dataset.sendEmailEndpointBase}/${this.adminLogId}/send-email`;
        this.setAdminSending(true);
        this.setAdminStatus('Sending…');

        try {
          // Multipart submit — no Content-Type header so the browser
          // sets the multipart boundary. `credentials: 'include'`
          // because this endpoint is admin-only: Shopify needs the
          // session cookie to populate `logged_in_customer_id` in the
          // signed proxy query string, which the backend uses for the
          // admin auth check. Multipart bodies don't trip Shopify's
          // JSON-POST intercept.
          const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'include',
            body: form,
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload.detail || `Send failed (HTTP ${response.status}).`);
          }
          window.QuoteCart?.clear();
          this.showSuccessView(this.adminQuoteNumber, /*admin*/ true);
        } catch (error) {
          console.error('[quote-page] admin send failed', error);
          this.setAdminStatus(error.message || 'Send failed. Try again.', true);
        } finally {
          this.setAdminSending(false);
        }
      }

      setAdminStatus(message, isError = false) {
        if (!this.adminStatusEl) return;
        this.adminStatusEl.textContent = message || '';
        this.adminStatusEl.classList.toggle('is-error', !!isError && !!message);
      }

      setAdminSending(isSending) {
        this.classList.toggle('is-admin-sending', isSending);
        if (this.adminSendBtn) this.adminSendBtn.disabled = isSending;
      }

      get billingAddressPayload() {
        // Only send a billing address when the checkbox is on. When off,
        // the backend defaults billing to the shipping address — see the
        // QBO mapping in services/qbo.py.
        if (!this.billingToggle?.checked) return null;
        const data = new FormData(this.formEl);
        return {
          street1: clean(data.get('billing_street1')),
          street2: clean(data.get('billing_street2')),
          city: clean(data.get('billing_city')),
          state: clean(data.get('billing_state')),
          zip_code: clean(data.get('billing_zip_code')),
          country: clean(data.get('billing_country')),
        };
      }

      get contactPayload() {
        const data = new FormData(this.formEl);
        return {
          name: clean(data.get('name')),
          email: clean(data.get('email')),
          phone: clean(data.get('phone')),
          company: clean(data.get('company')),
          street1: clean(data.get('street1')),
          street2: clean(data.get('street2')),
          city: clean(data.get('city')),
          state: clean(data.get('state')),
          zip_code: clean(data.get('zip_code')),
          country: clean(data.get('country')),
        };
      }

      validateRequiredFields() {
        const missing = Array.from(this.formEl.querySelectorAll('[required]')).filter((field) => {
          return !String(field.value || '').trim();
        });
        // typeMismatch on a `type="email"` input catches "no @ sign" plus
        // other malformed addresses. We only treat it as an *invalid*
        // email when the field is non-empty — an empty required email is
        // already covered by the missing-fields path above, and we don't
        // want to double up on the message.
        const emailField = this.formEl.querySelector('input[type="email"]');
        const malformedEmail =
          emailField &&
          String(emailField.value || '').trim().length > 0 &&
          emailField.validity?.typeMismatch
            ? emailField
            : null;

        // A started-but-unfinished phone ("(555) 5") is non-empty, so the
        // missing-fields path above waves it through. Customers have to give
        // a complete 10-digit number; admins never get `required` on the
        // field, so this check skips them.
        const phoneField = this.phoneInput?.hasAttribute('required') ? this.phoneInput : null;
        const incompletePhone =
          phoneField &&
          String(phoneField.value || '').trim().length > 0 &&
          String(phoneField.value).replace(/\D/g, '').length !== 10
            ? phoneField
            : null;

        // When the customer-carrier-account option is chosen, carrier +
        // account number are required. Service level stays optional.
        const carrierAccountIssues = this.validateCarrierAccountFields();

        if (
          missing.length === 0 &&
          !malformedEmail &&
          !incompletePhone &&
          carrierAccountIssues.fields.length === 0
        ) {
          this.clearStatus();
          return true;
        }

        const messageParts = [];

        if (missing.length > 0 || carrierAccountIssues.fields.length > 0) {
          const labels = missing.map((field) => {
            const label =
              field.closest('.field')?.querySelector('.field__label')?.textContent?.trim() ||
              field.name;
            // Shipping + billing share the same field labels ("Street
            // address", "City", ...), so prefix billing fields to avoid
            // an ambiguous "Please complete: Street address, Street
            // address" when both are blank.
            return field.name?.startsWith('billing_') ? `billing ${label.toLowerCase()}` : label;
          });
          labels.push(...carrierAccountIssues.labels);
          messageParts.push(`Please complete: ${labels.join(', ')}.`);
        }
        if (malformedEmail) {
          messageParts.push('Please enter a valid email address (must include @).');
        }
        if (incompletePhone) {
          messageParts.push('Please enter a complete 10-digit phone number.');
        }

        this.showError(messageParts.join(' '));
        (missing[0] || malformedEmail || incompletePhone || carrierAccountIssues.fields[0])?.focus();
        return false;
      }

      validateCarrierAccountFields() {
        if (this.selectedShippingHandle !== CARRIER_ACCOUNT_VALUE) {
          return { fields: [], labels: [] };
        }
        const fields = [];
        const labels = [];
        if (!clean(this.carrierSelect?.value)) {
          fields.push(this.carrierSelect);
          labels.push('carrier');
        }
        if (!clean(this.carrierAccountInput?.value)) {
          fields.push(this.carrierAccountInput);
          labels.push('account number');
        }
        return { fields, labels };
      }

      formatPhoneInput(event) {
        event.currentTarget.value = formatPhone(event.currentTarget.value);
      }

      showSuccessView(quoteNumber, isAdmin = false) {
        if (this.formEl) this.formEl.hidden = true;
        if (this.itemsEl) this.itemsEl.hidden = true;
        if (this.emptyEl) this.emptyEl.hidden = true;
        if (this.adminPanel) this.adminPanel.hidden = true;
        if (this.successEl) this.successEl.hidden = false;
        // Two success blocks live in the success container — one for
        // the customer ("you'll get an email shortly") and one for the
        // admin ("email sent"). Swap them in-place rather than building
        // separate success views.
        const customerBlock = this.successEl?.querySelector('.quote-page__success-customer');
        const adminBlock = this.successEl?.querySelector('.quote-page__success-admin');
        if (customerBlock) customerBlock.hidden = isAdmin;
        if (adminBlock) adminBlock.hidden = !isAdmin;
        // Populate the quote number on whichever block is active. The
        // backend always returns a quote number now (synchronously,
        // after the QBO Estimate is created); the placeholder hides
        // gracefully if it ever comes back empty.
        const selector = isAdmin
          ? '[data-quote-success-number-admin]'
          : '[data-quote-success-number]';
        const numberEl = this.successEl?.querySelector(selector);
        if (numberEl) numberEl.textContent = quoteNumber || '';
      }

      setSubmitting(isSubmitting) {
        this.classList.toggle('is-submitting', isSubmitting);
        if (this.submitBtn) this.submitBtn.disabled = isSubmitting;
        if (this.loadingOverlay) this.loadingOverlay.hidden = !isSubmitting;
      }

      clearStatus() {
        if (!this.statusEl) return;
        this.statusEl.textContent = '';
        this.statusEl.classList.remove('is-error');
      }

      showError(message) {
        if (!this.statusEl) return;
        this.statusEl.textContent = message;
        this.statusEl.classList.add('is-error');
      }

      showSubmitFailure() {
        // Submit-failure copy is the only place we need to render an
        // anchor inside the status line, so build the DOM nodes by hand
        // rather than reaching for innerHTML — keeps the showError path
        // (which takes plain strings from validation paths) safe from
        // XSS as a class.
        if (!this.statusEl) return;
        this.statusEl.replaceChildren();
        this.statusEl.classList.add('is-error');
        this.statusEl.append(
          document.createTextNode("We couldn’t send your quote request. Please try again or "),
        );
        const link = document.createElement('a');
        link.href = 'https://stack-light.com/pages/contact-us';
        link.textContent = 'contact us';
        this.statusEl.append(link);
        this.statusEl.append(document.createTextNode(' for a quote.'));
      }
    }
  );
}

function clean(value) {
  const cleaned = String(value || '').trim();
  return cleaned || null;
}

function formatPhone(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatPrice(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
}
